import { m } from '@pricedrop/i18n';
import BellRing from 'lucide-react/dist/esm/icons/bell-ring';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useSession } from '@/lib/auth-client';
import { useAuthModal } from '@/lib/auth-modal-context';
import {
  getNotificationButtonText,
  type Notification,
  useItemNotification,
} from '@/lib/notifications';
import { ItemNotificationDialog } from './item-notification-dialog';

interface GetNotificationsButtonProps {
  itemUin: number;
  itemName: string;
  currentPrice: number | null;
  isInStock: boolean;
  compact?: boolean;
  existingNotification?: Notification | null;
}

export function GetNotificationsButton({
  itemUin,
  itemName,
  currentPrice,
  isInStock,
  compact = false,
  existingNotification,
}: GetNotificationsButtonProps) {
  const { data: session } = useSession();
  const { requireAuth } = useAuthModal();

  const [dialogOpen, setDialogOpen] = useState(false);

  const shouldFetchIndividually = existingNotification === undefined;
  const { data: fetchedNotification } = useItemNotification(
    itemUin,
    shouldFetchIndividually && !!session,
  );
  const notification = shouldFetchIndividually
    ? (fetchedNotification ?? null)
    : existingNotification;

  const handleClick = () => {
    requireAuth(() => setDialogOpen(true), {
      title: m.notifications_auth_title(),
      description: m.notifications_auth_description(),
    });
  };

  const buttonText = getNotificationButtonText(notification, isInStock);

  return (
    <>
      <Button
        onClick={handleClick}
        className={
          compact
            ? 'bg-gradient-to-r from-orange-500 to-orange-600 border-0 text-white font-medium transition-all duration-300 hover:from-orange-600 hover:to-orange-700 hover:shadow-md hover:shadow-orange-500/20 hover:-translate-y-0.5 cursor-pointer px-3'
            : 'flex h-12 w-full flex-row items-center gap-2 rounded bg-gradient-to-r from-yellow-600 to-orange-600 py-2 text-center font-semibold tracking-widest text-white hover:to-emerald-400 md:h-full'
        }
      >
        <BellRing className="h-4 w-4" />
        {!compact && buttonText}
      </Button>

      <ItemNotificationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        itemUin={itemUin}
        itemName={itemName}
        currentPrice={currentPrice}
        existingNotification={notification}
      />
    </>
  );
}
